'use client'

import { coreResource } from '@/libs/config/client'
import { parseLocale } from '@/utils/localisation'
import { T, useTranslate } from '@tolgee/react'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'

import IconOddle from '@/components/icons/IconOddle'
import IconOddleEatsSquare from '@/components/icons/IconOddleEatsSquare'
import { useGetCtaConfigQuery } from '@/services/local'
import { useSelector } from 'react-redux'
import { selectMeta } from '@/redux/meta'

export const errorTypes = {
  '404': '404',
  '500': '500',
  menuNotFound: 'menuNotFound',
  pageNotFound: 'pageNotFound',
  sectionNotFound: 'sectionNotFound',
  productNotFound: 'productNotFound',
  menuNotAvailable: 'menuNotAvailable',
  checkoutDirectAccess: 'checkoutDirectAccess',
  accountSuspended: 'accountSuspended',
  paymentPaid: 'paymentPaid',
  paymentLinkInvalid: 'paymentLinkInvalid',
  orderHasBeenConfirmed: 'orderHasBeenConfirmed',
  orderHasBeenCancelled: 'orderHasBeenCancelled',
  orderHasBeenFulfilled: 'orderHasBeenFulfilled',
  temporarilyUnavailable: 'temporarilyUnavailable',
  storeDeleted: 'storeDeleted',
  storeNotEnablePickupDelivery: 'storeNotEnablePickupDelivery',
  storeNoPaymentOptionPickup: 'storeNoPaymentOptionPickup',
  storeNoTimeOption: 'storeNoTimeOption',
  storeNotEnablePickup: 'storeNotEnablePickup',
  storeLocationNotFound: 'storeLocationNotFound',
  storeNoPaymentOptionDelivery: 'storeNoPaymentOptionDelivery',
  storeNotEnableDelivery: 'storeNotEnableDelivery',
  storeNotOpen: 'storeNotOpen',
  menuIsEmpty: 'menuIsEmpty',
} as const

export type ErrorType = keyof typeof errorTypes

const getErrorType = (error?: Oms.Error) => {
  if (error) {
    const omsError = Array.isArray(error) ? error[0] : error
    switch (omsError.errorCode) {
      case 'MenuNotFound':
      case 'SiteNotFound':
        return errorTypes.menuNotFound
      case 'StoreDeleted':
        return errorTypes.storeDeleted
      case 'StoreNotEnablePickupDelivery':
        return errorTypes.storeNotEnablePickupDelivery
      case 'StoreNoPaymentOptionPickup':
        return errorTypes.storeNoPaymentOptionPickup
      case 'StoreNoTimeOption':
        return errorTypes.storeNoTimeOption
      case 'StoreNotEnablePickup':
        return errorTypes.storeNotEnablePickup
      case 'StoreLocationNotFound':
        return errorTypes.storeLocationNotFound
      case 'StoreNoPaymentOptionDelivery':
        return errorTypes.storeNoPaymentOptionDelivery
      case 'StoreNotEnableDelivery':
        return errorTypes.storeNotEnableDelivery
      case 'StoreNotOpen':
        return errorTypes.storeNotOpen
      case 'MenuNotAvailable':
        return errorTypes.menuNotAvailable
      case 'TemporarilyUnavailable':
        return errorTypes.temporarilyUnavailable
      case 'AccountSuspended':
        return errorTypes.accountSuspended
      case 'MenuIsEmpty':
        return errorTypes.menuIsEmpty
      case '404':
        return errorTypes['404']
      case '500':
        return errorTypes['500']
      default:
        return omsError.errorCode
    }
  }
  return null
}

const clearAllCookies = () => {
  const cookies = document.cookie.split('; ')
  for (let c = 0; c < cookies.length; c++) {
    const d = [...window.location.hostname.split('.'), '']
    while (d.length > 0) {
      const cookieName = encodeURIComponent(
        cookies[c]?.split(';')[0]?.split('=')[0] || ''
      )
      let cookieBase = `${cookieName}=; expires=Thu, 01-Jan-1970 00:00:01 GMT; `
      if (d.length > 0) {
        cookieBase += `domain=${d.filter((x) => x).join('.')}; `
      }
      cookieBase += 'path='
      const p = window.location.pathname.split('/')
      document.cookie = cookieBase + '/'
      while (p.length > 0) {
        document.cookie = cookieBase + p.join('/')
        p.pop()
      }
      d.shift()
    }
  }
}

const onReset = async (onClickReset?: () => Promise<void>) => {
  if (onClickReset) {
    await onClickReset()
  }
  clearAllCookies()
  window.location.reload()
}

const onLogout = () => {
  window.localStorage.clear()
  clearAllCookies()
  window.location.href = window.location.origin
}

interface ErrorProps {
  locale?: string
  type?: ErrorType
  packages?: 'dashboard' | 'fesc' | 'feastbump'
  ctaLink?: string
  onClickReset?: () => Promise<void>
  errorDetails?: Oms.Error
}

const LinkedOddleEatsButton = ({ ctaLink }: { ctaLink?: string }) => {
  const { t } = useTranslate()
  const { data: ctaConfigResp } = useGetCtaConfigQuery(undefined)
  const metaState = useSelector(selectMeta)

  const ctaSpecificConfig = metaState?.cartName
    ? ctaConfigResp?.data?.[metaState.cartName]
    : undefined

  return (
    <>
      <IconOddleEatsSquare className="!h-[60px] !w-[190px]" />
      <Link
        href={ctaSpecificConfig || ctaLink || 'https://eats.oddle.me'}
        className="mt-4 block"
        rel="nofollow"
      >
        <button className="bg-brand-500 hover:bg-brand-600 rounded-md px-6 py-2 text-white transition-colors">
          {t('Take me to ALL the good food!')}
        </button>
      </Link>
    </>
  )
}

const Error = ({
  locale = 'en_SG',
  packages = 'fesc',
  ctaLink,
  onClickReset,
  errorDetails,
}: ErrorProps) => {
  const { t } = useTranslate()
  const [showErrorDetails, setShowErrorDetails] = useState(false)
  const { country } = parseLocale(locale)
  const type = getErrorType(errorDetails)

  const getErrorContent = () => {
    switch (type) {
      case errorTypes['404']:
        return {
          title: t('Page Not Found'),
          text: (
            <h4 className="text-lg font-medium text-gray-900">
              {t(
                'The link you clicked may be broken or the page may have been removed.'
              )}
            </h4>
          ),
          mainImage: (
            <Image
              src={coreResource('/images/Error_404.png')}
              alt={t('Error 404')}
              width={400}
              height={300}
              className="mx-auto w-full max-w-md"
            />
          ),
          cta: (
            <Link href="/" className="mt-4 block">
              <button className="bg-brand-500 hover:bg-brand-600 rounded-md px-6 py-2 text-white transition-colors">
                {t('Go to Homepage')}
              </button>
            </Link>
          ),
        }
      case errorTypes['500']:
        return {
          title: t('Something went wrong!'),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t(
                  'We are working to bring this Shop back online as soon as we can!'
                )}
              </h4>
              <h4 className="text-lg font-medium text-gray-900">
                {t(
                  'The restaurant and its partners are working hard to prepare and fulfil the orders that have already been placed prior to the Shop being offline.'
                )}
              </h4>
              {packages === 'fesc' && (
                <>
                  <h4 className="text-lg font-medium text-gray-900">
                    <T
                      keyName="You may try refreshing or <clickingHere></clickingHere> to refresh the browser session."
                      params={{
                        clickingHere: (
                          <button
                            onClick={() => onReset(onClickReset)}
                            className="text-brand-500 hover:text-brand-600 underline"
                          >
                            {t('clicking here')}
                          </button>
                        ),
                      }}
                      defaultValue="You may try refreshing or <clickingHere></clickingHere> to refresh the browser session."
                    />
                  </h4>
                  <p className="text-sm italic text-gray-600">
                    {t(
                      'This action will log you out and clear your shopping cart.'
                    )}
                  </p>
                </>
              )}
              <h4 className="text-lg font-medium text-gray-900">
                {t(
                  "If the above doesn't work, please revisit this page in 15 minutes."
                )}
              </h4>
            </div>
          ),
          mainImage: (
            <Image
              src={coreResource('/images/Error_500.png')}
              alt={t('Error 500')}
              width={400}
              height={300}
              className="mx-auto w-full max-w-md"
            />
          ),
        }
      case errorTypes.temporarilyUnavailable:
        return {
          title: t('Something went wrong!'),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t(
                  "This Shop is temporarily unavailable and we're working to bring this Shop back online as soon as we can!"
                )}
              </h4>
              <p className="text-sm italic text-gray-600">
                {t(
                  'In the meanwhile, you may\n1. Refresh this page (it works for some of the visitors).\n2. Visit this page in 15 minutes.'
                )}
              </p>
            </div>
          ),
          mainImage: (
            <Image
              src={coreResource('/images/Error_500.png')}
              alt={t('Error 500')}
              width={400}
              height={300}
              className="mx-auto w-full max-w-md"
            />
          ),
        }
      case errorTypes.menuNotFound:
        return {
          title: t('Domain Not Found'),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t('This domain is currently unclaimed on Oddle.')}
              </h4>
              <h4 className="text-lg font-medium text-gray-900">
                <T
                  keyName="If you are a restaurant owner and would like to claim this domain, please contact <link></link>."
                  params={{
                    link: (
                      <Link
                        href="mailto:support@oddle.me"
                        className="text-brand-500 hover:text-brand-600 underline"
                      >
                        support@oddle.me
                      </Link>
                    ),
                  }}
                />
              </h4>
              <h4 className="text-lg font-medium text-gray-900">
                {t(
                  'Alternatively, there are more than 500 restaurants at Oddle Eats.'
                )}
              </h4>
            </div>
          ),
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          cta: <LinkedOddleEatsButton ctaLink={ctaLink} />,
        }
      case errorTypes.pageNotFound:
        return {
          title: t('Page Not Found'),
          text: (
            <h4 className="text-lg font-medium text-gray-900">
              {t(
                'The link you clicked may be broken or the page may have been removed.'
              )}
            </h4>
          ),
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
        }
      case errorTypes.sectionNotFound:
        return {
          title: t('Oops..'),
          text: (
            <h4 className="text-lg font-medium text-gray-900">
              {t('We are having trouble loading this page.')}
            </h4>
          ),
          mainImage: (
            <Image
              src={coreResource('/images/Error_500.png')}
              alt={t('Error 500')}
              width={400}
              height={300}
              className="mx-auto w-full max-w-md"
            />
          ),
        }
      case errorTypes.productNotFound:
        return {
          title: t('Product Not Found'),
          text: (
            <h4 className="text-lg font-medium text-gray-900">
              {t('Product is no longer available.')}
            </h4>
          ),
          mainImage: (
            <Image
              src={coreResource('/images/Error_404.png')}
              alt={t('Error 404')}
              width={400}
              height={300}
              className="mx-auto w-full max-w-md"
            />
          ),
          cta: (
            <Link href="/" className="mt-4 block">
              <button className="bg-brand-500 hover:bg-brand-600 rounded-md px-6 py-2 text-white transition-colors">
                {t('Go to Homepage')}
              </button>
            </Link>
          ),
        }
      case errorTypes.menuNotAvailable:
        return {
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          title: t("We'll be back online soon!"),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t('Menu is unavailable.')}
              </h4>
              <h4 className="text-lg font-medium text-gray-900">
                {t(
                  'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                )}
              </h4>
            </div>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
      case errorTypes.accountSuspended:
        return {
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          title: t("We'll be back soon!"),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t("If you're a visitor, please try again later.")}
              </h4>
              <h4 className="text-lg font-medium text-gray-900">
                {t("If you're the owner of this store, please contact ")}
                <Link
                  href="mailto:support@oddle.me"
                  className="text-brand-500 hover:text-brand-600 underline"
                >
                  Oddle
                </Link>
              </h4>
              {country === 'SG' && (
                <h4 className="text-lg font-medium text-gray-900">
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </h4>
              )}
            </div>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
      case errorTypes.checkoutDirectAccess:
        return {
          title: t('Order is not ready for checkout.'),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t('Please return to the ordering page and checkout again.')}
              </h4>
              <h4 className="text-lg font-medium text-gray-900">
                <T
                  keyName="If you are still facing this problem, <clickHere></clickHere> to refresh the browser session (this action will log you out and clear your shopping cart)."
                  params={{
                    clickHere: (
                      <button
                        onClick={onLogout}
                        className="text-brand-500 hover:text-brand-600 underline"
                      >
                        {t('click here')}
                      </button>
                    ),
                  }}
                  defaultValue="If you are still facing this problem, <clickHere></clickHere> to refresh the browser session (this action will log you out and clear your shopping cart)."
                />
              </h4>
            </div>
          ),
          cta: (
            <Link href="/" className="mt-4 block">
              <button className="bg-brand-500 hover:bg-brand-600 rounded-md px-6 py-2 text-white transition-colors">
                {t('Take me back')}
              </button>
            </Link>
          ),
        }
      case errorTypes.paymentPaid:
        return {
          title: t('Payment has been made.'),
          text: (
            <h4 className="text-lg font-medium text-gray-900">
              {t('You are not required to make payment for this order.')}
            </h4>
          ),
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          cta: (
            <Link href="/" className="mt-4 block">
              <button className="bg-brand-500 hover:bg-brand-600 rounded-md px-6 py-2 text-white transition-colors">
                {t('Take me back')}
              </button>
            </Link>
          ),
        }
      case errorTypes.paymentLinkInvalid:
        return {
          title: t('Payment Link may have expired.'),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t(
                  'You may\n1. Check that the payment link is correct, or\n2. Request for a new payment link from the restaurant.'
                )}
              </h4>
            </div>
          ),
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          cta: (
            <Link href="/" className="mt-4 block">
              <button className="bg-brand-500 hover:bg-brand-600 rounded-md px-6 py-2 text-white transition-colors">
                {t('Take me back')}
              </button>
            </Link>
          ),
        }
      case errorTypes.orderHasBeenConfirmed:
        return {
          title: t('Oops...'),
          text: (
            <h4 className="text-lg font-medium text-gray-900">
              {t('This order has been processed by the restaurant partner')}
            </h4>
          ),
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
        }
      case errorTypes.orderHasBeenFulfilled:
        return {
          title: t('Oops...'),
          text: (
            <h4 className="text-lg font-medium text-gray-900">
              {t('You should have received your order by now')}
            </h4>
          ),
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
        }
      case errorTypes.orderHasBeenCancelled:
        return {
          title: t('Oops...'),
          text: (
            <h4 className="text-lg font-medium text-gray-900">
              {t('This order has been cancelled')}
            </h4>
          ),
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
        }
      case errorTypes.storeDeleted:
        return {
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          title: t('Sorry, this store is currently closed.'),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t(
                  'No stores are available to accept delivery and takeaway orders.'
                )}
              </h4>
              {country === 'SG' && (
                <h4 className="text-lg font-medium text-gray-900">
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </h4>
              )}
            </div>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
      case errorTypes.storeNotEnablePickupDelivery:
        return {
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          title: t('Sorry, this store is currently closed.'),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t(
                  'No stores are available to accept delivery and takeaway orders.'
                )}
              </h4>
              {country === 'SG' && (
                <h4 className="text-lg font-medium text-gray-900">
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </h4>
              )}
            </div>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
      case errorTypes.storeNoPaymentOptionPickup:
        return {
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          title: t("We'll be back online soon!"),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t('Payments cannot be accepted for pickup orders.')}
              </h4>
              {country === 'SG' && (
                <h4 className="text-lg font-medium text-gray-900">
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </h4>
              )}
            </div>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
      case errorTypes.storeNoTimeOption:
        return {
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          title: t("We'll be back online soon!"),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t('There are no available delivery and pickup timeslots.')}
              </h4>
              {country === 'SG' && (
                <h4 className="text-lg font-medium text-gray-900">
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </h4>
              )}
            </div>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
      case errorTypes.storeNotEnablePickup:
        return {
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          title: t("We'll be back online soon!"),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t('Pickup orders are not accepted.')}
              </h4>
              {country === 'SG' && (
                <h4 className="text-lg font-medium text-gray-900">
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </h4>
              )}
            </div>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
      case errorTypes.storeLocationNotFound:
        return {
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          title: t("We'll be back online soon!"),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t('Delivery service is not available.')}
              </h4>
              {country === 'SG' && (
                <h4 className="text-lg font-medium text-gray-900">
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </h4>
              )}
            </div>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
      case errorTypes.storeNoPaymentOptionDelivery:
        return {
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          title: t("We'll be back online soon!"),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t('Payments cannot be accepted for delivery orders.')}
              </h4>
              {country === 'SG' && (
                <h4 className="text-lg font-medium text-gray-900">
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </h4>
              )}
            </div>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
      case errorTypes.storeNotEnableDelivery:
        return {
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          title: t("We'll be back online soon!"),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t('Delivery orders are not accepted.')}
              </h4>
              {country === 'SG' && (
                <h4 className="text-lg font-medium text-gray-900">
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </h4>
              )}
            </div>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
      case errorTypes.storeNotOpen:
        return {
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          title: t('Sorry, this Shop is currently closed.'),
          text:
            country === 'SG' ? (
              <h4 className="text-lg font-medium text-gray-900">
                {t(
                  'No stores are available to accept delivery and takeaway orders.'
                )}
              </h4>
            ) : null,
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
      case errorTypes.menuIsEmpty:
        return {
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          title: t('Sorry, this store is currently closed.'),
          text: (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">
                {t('No items are available for ordering')}
              </h4>
              {country === 'SG' && (
                <h4 className="text-lg font-medium text-gray-900">
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </h4>
              )}
            </div>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
      default:
        return {
          title: t('Something went wrong!'),
          text: (
            <div className="space-y-4">
              <div className="w-full rounded-lg bg-gray-50">
                <button
                  onClick={() => setShowErrorDetails(!showErrorDetails)}
                  className="flex w-full items-center justify-between p-4"
                >
                  <h4 className="text-lg font-medium text-gray-900">
                    {t(
                      'A clue for the Oddle team to resolve this issue faster'
                    )}
                  </h4>
                  <svg
                    className={`h-5 w-5 transform transition-transform ${
                      showErrorDetails ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {showErrorDetails && (
                  <div className="mt-2 p-4">
                    <p className="text-gray-900">
                      {errorDetails?.errorDescription
                        ? t(errorDetails.errorDescription)
                        : errorDetails?.errorCode}
                    </p>
                  </div>
                )}
              </div>
              <h4 className="text-lg font-medium text-gray-900">
                {t(
                  'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                )}
              </h4>
            </div>
          ),
          mainImage: <IconOddle className="!h-[300px] !w-[400px]" />,
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton ctaLink={ctaLink} />
            ) : null,
        }
    }
  }

  const { title, text, mainImage, cta } = getErrorContent()

  return (
    <div className="font-roboto flex min-h-screen w-full items-center justify-center bg-white text-center">
      <Head>
        <title>{title}</title>
        <link
          href="https://fonts.googleapis.com/css?family=Roboto"
          rel="stylesheet"
        />
      </Head>
      <div className="relative h-[100vh] w-full">
        <div className="absolute inset-0 flex justify-between">
          <div className="-ml-20 hidden md:block">
            <Image
              src={coreResource('/images/Background_Yoghurt.png')}
              alt={t('Background Yoghurt')}
              width={300}
              height={400}
              className="object-contain"
            />
          </div>
          <div className="hidden md:block">
            <Image
              src={coreResource('/images/Background_Bread.png')}
              alt={t('Background Bread')}
              width={300}
              height={400}
              className="object-contain"
            />
          </div>
        </div>
        <div className="relative mx-auto flex h-[100vh] max-w-[80%] flex-col items-center justify-center md:max-w-[60%] lg:max-w-[42%]">
          {mainImage}
          {title && <h1 className="mb-4 mt-4 text-3xl font-bold">{title}</h1>}
          {text}
          {cta}
        </div>
      </div>
    </div>
  )
}

export default Error
